import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, MessageCircle } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useApp } from "@/context/AppContext";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const { t } = useApp();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 15);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServiceOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!serviceOpen) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServiceOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [serviceOpen]);

  const navLinks = [
    { label: t.nav.features, href: "/#features" },
    { label: t.nav.howItWorks, href: "/#how-it-works" },
    { label: t.nav.gallery, href: "/#gallery" },
    { label: t.nav.pricing, href: "/#pricing" },
    { label: t.nav.faq, href: "/#faq" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${scrolled
          ? "backdrop-blur-xl bg-[#FDFBF7]/90 border-b border-amber-900/10 shadow-[0_4px_25px_-5px_rgba(217,119,6,0.08)]"
          : "bg-[#FDFBF7]/80 border-b border-amber-900/5 backdrop-blur-md"
        }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        {/* Brand Logo */}
        <Logo />

        {/* Desktop Nav Links */}
        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {navLinks.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="px-3.5 py-2 text-sm font-semibold text-zinc-700 hover:text-zinc-950 hover:bg-amber-500/10 rounded-xl transition-colors"
            >
              {item.label}
            </a>
          ))}

          {/* Ekosistem Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setServiceOpen((v) => !v)}
              className="px-3.5 py-2 inline-flex items-center gap-1 text-sm font-semibold text-zinc-700 hover:text-zinc-950 hover:bg-amber-500/10 rounded-xl transition-colors"
            >
              {t.nav.ecosystem}
              <ChevronDown
                className={`h-3.5 w-3.5 text-zinc-500 transition-transform duration-200 ${serviceOpen ? "rotate-180 text-amber-600" : ""
                  }`}
              />
            </button>

            {serviceOpen && (
              <div className="absolute left-0 top-full mt-2 w-64 rounded-2xl border border-amber-200/90 bg-white shadow-2xl shadow-amber-900/10 p-2 animate-fade-up">
                <a
                  href="#gallery"
                  onClick={() => setServiceOpen(false)}
                  className="flex flex-col p-2.5 rounded-xl hover:bg-amber-50/80 transition-colors"
                >
                  <span className="text-sm font-bold text-zinc-900">{t.nav.erp.title}</span>
                  <span className="text-xs text-zinc-500 mt-0.5">{t.nav.erp.desc}</span>
                </a>
                <a
                  href="#demo"
                  onClick={() => setServiceOpen(false)}
                  className="flex flex-col p-2.5 rounded-xl hover:bg-amber-50/80 transition-colors"
                >
                  <span className="text-sm font-bold text-zinc-900">{t.nav.academy.title}</span>
                  <span className="text-xs text-zinc-500 mt-0.5">{t.nav.academy.desc}</span>
                </a>
                <a
                  href="#demo"
                  onClick={() => setServiceOpen(false)}
                  className="flex flex-col p-2.5 rounded-xl hover:bg-amber-50/80 transition-colors"
                >
                  <span className="text-sm font-bold text-zinc-900">{t.nav.community.title}</span>
                  <span className="text-xs text-zinc-500 mt-0.5">{t.nav.community.desc}</span>
                </a>
              </div>
            )}
          </div>
        </nav>

        {/* Right CTA Actions */}
        <div className="hidden lg:flex items-center gap-3.5">
          {/* Animated Language Switcher Capsule */}
          <LanguageSwitcher />

          {/* WhatsApp Action */}
          <a
            href="https://wa.me/6285183003076?text=Halo%20AstaEnterprise%2C%20saya%20tertarik%20konsultasi%20software%20toko%20emas"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-zinc-700 hover:text-zinc-950 transition-colors"
          >
            <MessageCircle className="h-4 w-4 text-emerald-600" />
            <span>{t.nav.whatsapp}</span>
          </a>

          {/* Primary Demo Button */}
          <a href="#demo">
            <Button className="h-10 px-5 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold text-sm shadow-[0_4px_16px_rgba(217,119,6,0.3)] transition-all">
              {t.nav.requestDemo}
            </Button>
          </a>
        </div>

        {/* Mobile menu button */}
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 rounded-xl text-zinc-700 hover:text-zinc-950 hover:bg-black/5 transition-colors"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Drawer */}
      {open && (
        <div className="lg:hidden border-t border-amber-200/80 bg-[#FDFBF7]/98 backdrop-blur-2xl px-6 py-6 space-y-4 animate-fade-up shadow-xl">
          <div className="flex items-center justify-between pb-3 border-b border-zinc-200/80">
            <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Pilih Bahasa</span>
            <LanguageSwitcher />
          </div>

          <nav className="flex flex-col space-y-2">
            {navLinks.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={() => setOpen(false)}
                className="px-3 py-2 text-base font-semibold text-zinc-800 hover:text-zinc-950 hover:bg-amber-50 rounded-xl transition-colors"
              >
                {item.label}
              </a>
            ))}
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="px-3 py-2 text-base font-semibold text-zinc-800 hover:text-zinc-950 hover:bg-amber-50 rounded-xl transition-colors"
            >
              {t.nav.erp.title}
            </Link>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="px-3 py-2 text-base font-semibold text-zinc-800 hover:text-zinc-950 hover:bg-amber-50 rounded-xl transition-colors"
            >
              {t.nav.contact}
            </Link>
          </nav>

          <div className="pt-4 border-t border-zinc-200 flex flex-col gap-3">
            <a
              href="https://wa.me/6285183003076?text=Halo%20AstaEnterprise%2C%20saya%20tertarik%20konsultasi%20software%20toko%20emas"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 h-11 rounded-full border border-zinc-300 text-zinc-800 text-sm font-semibold hover:bg-white transition-colors bg-white/60 shadow-sm"
            >
              <MessageCircle className="h-4 w-4 text-emerald-600" />
              Chat WhatsApp
            </a>
            <a href="#demo" onClick={() => setOpen(false)}>
              <Button className="w-full h-11 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold text-sm hover:from-amber-600 hover:to-amber-700 shadow-md">
                {t.nav.requestDemo}
              </Button>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
